import {
  campaignsApi,
  CreateCampaignParams,
  getCampaignLeadCostQueryOptions,
  simpleCampaignsQueryOptions,
} from "@/api/campaign.api";
import { creditsQueryOptions } from "@/api/credits.api";
import { useUsersQuery } from "@/api/users.api";
import { SegmentHeader } from "@/components/campaign/basic/SegmentHeader";
import { ConfirmBoostDialog } from "@/components/campaign/ConfirmBoostDialog";
import { ContactInformationFormValues } from "@/components/campaign/setup/planning/ContactInformationForm";
import { Separator } from "@/components/generic/Separator";
import { PageHeader } from "@/components/layout/PageHeader";
import { PRIORITY_OPTIONS } from "@/data/campaign-priorities";
import { getFilterGroupById } from "@/data/filters/config";
import { useAvailableIntegrationsQuery } from "@/queries/integrations";
import { newlyCreatedCampaignIdAtom } from "@/routes/_dashboard/campaigns/-state";
import {
  trackDeliveryScheduled,
  trackEvent,
} from "@/services/amplitude.service";
import {
  getCampaignPriority,
  getFilterGroupsFromConfigurations,
} from "@/services/configurations.service";
import { getCampaignUpdateCreditStats } from "@/services/credits.service";
import {
  NewCampaignState,
  newCampaignStateAtom,
  NewCampaignStateDelivery,
} from "@/services/new-campaign.service";
import { markExpectedCampaignRun } from "@/services/push-message-fallback.service";
import { formatScheduleLabel } from "@/services/schedule.service";
import { deliveryTypes, planningTypes } from "@/types/campaign";
import { DeferredPromise } from "@/types/deferred-promise";
import { queryClient } from "@/utils/api";
import { entriesToRecord } from "@/utils/array-utils";
import { getFinalCampaignName } from "@/utils/campaign-helpers";
import { detectCurrentStrategy } from "@/utils/configurations";
import { formatDate } from "@/utils/dates";
import { store } from "@/utils/jotai";
import { formatNumber } from "@/utils/number-utils";
import { isOneOf } from "@/utils/type-guards";
import { getLocalTimeZone, today } from "@internationalized/date";
import * as Sentry from "@sentry/react";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, NavigateFn, redirect } from "@tanstack/react-router";
import { RESET } from "jotai/utils";
import React, {
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import z from "zod";
import { Route as BuyCreditsRoute } from "./buy-credits";

type SummaryRow = { title: string; value: string };
type Summary = { title: string; rows: SummaryRow[] }[];

function doesCampaignStartInstantly(newCampaignState: NewCampaignState) {
  if (!newCampaignState.planning) return false;

  switch (newCampaignState.planning.type) {
    case "OnDemand":
      return true;

    case "Scheduled":
    case "Triggered": {
      const { chosenStartDate, chosenEndDate } = newCampaignState.planning;

      if (chosenStartDate) {
        const todayDate = today(getLocalTimeZone());

        if (
          chosenStartDate.compare(todayDate) <= 0 &&
          (chosenEndDate === null || chosenEndDate.compare(todayDate) > 0)
        ) {
          return true;
        }
      }
    }
  }

  return false;
}

const historyStateSchema = z
  .object({
    autoSubmit: z.boolean().optional(),
  })
  .optional()
  .catch(undefined);

const parseHistoryState = (v: z.infer<typeof historyStateSchema>) =>
  historyStateSchema.parse(v);

export const Route = createFileRoute("/_dashboard/campaigns/setup/summary/")({
  component: RouteComponent,
  staticData: {
    meta: {
      title: "Opsummering",
    },
    crumb: "Opsummering",
  },
  beforeLoad: ({ context, location }) => {
    const historyState = parseHistoryState(
      location.state.routeStates?.[Route.id],
    );

    const createCampaign = async ({
      ignoreCredits = false,
      navigate,
      confirm,
    }: {
      ignoreCredits?: boolean;
      navigate?: NavigateFn;
      confirm: (maxCredits: number) => Promise<boolean>;
    }) => {
      const newCampaignState = store.get(newCampaignStateAtom);

      if (!newCampaignState.planning || !newCampaignState.delivery)
        throw Error("Incomplete newCampaignState");

      const simpleCampaigns = await queryClient.ensureQueryData(
        simpleCampaignsQueryOptions,
      );

      const contactInformationValues = {
        deliverContacts: newCampaignState.planning.deliverContacts ?? false,
        deliverContactsRequirePhone:
          newCampaignState.planning.deliverContactsRequirePhone ?? false,
        deliverLiveNumber: newCampaignState.planning.deliverLiveNumber ?? false,
      } satisfies ContactInformationFormValues;

      const createCampaignParams = {
        name: getFinalCampaignName(
          newCampaignState.name ?? "",
          simpleCampaigns,
        ),
        responsibleId: newCampaignState.responsibleId,

        configurations: newCampaignState.configurations,
        stopLists: newCampaignState.stopLists ?? [],

        type: newCampaignState.planning.type,
        schedules: newCampaignState.planning.schedules ?? [],
        triggers: newCampaignState.planning.triggers ?? [],
        maxLeadsPerWeek: newCampaignState.planning.maxLeadsPerWeek ?? null,
        chosenStartDate: newCampaignState.planning.chosenStartDate
          ? newCampaignState.planning.chosenStartDate.toString()
          : null,
        chosenEndDate: newCampaignState.planning.chosenEndDate
          ? newCampaignState.planning.chosenEndDate.toString()
          : null,

        ...(newCampaignState.planning.type !== "OnDemand"
          ? contactInformationValues
          : {}),

        exportMode: newCampaignState.delivery.exportMode ?? null,
        exportSystem: newCampaignState.delivery.exportSystem ?? null,
        exportInput: newCampaignState.delivery.exportInput ?? null,
        completionEmail: newCampaignState.delivery.completionEmail,
      } satisfies CreateCampaignParams;

      if (!ignoreCredits && doesCampaignStartInstantly(newCampaignState)) {
        const [credits, campaignLeadCost] = await Promise.all([
          context.queryClient.ensureQueryData(creditsQueryOptions),
          context.queryClient.ensureQueryData(
            getCampaignLeadCostQueryOptions({
              ...createCampaignParams,
              status: "Active",
              ...contactInformationValues,
            }),
          ),
        ]);

        const {
          updatedCreditsNeededRestOfPeriod,
          updatedCreditsNeededNextPeriod,
        } = getCampaignUpdateCreditStats({
          credits,
          campaignLeadCost: {
            leadCost: 0,
            creditEstimateRestOfPeriod: 0,
            creditEstimateNextPeriod: 0,
          },
          updatedCampaignLeadCost: campaignLeadCost,
        });

        const neededCredits = Math.max(
          updatedCreditsNeededRestOfPeriod,
          updatedCreditsNeededNextPeriod,
        );

        if (neededCredits) {
          const navigateOptions = {
            from: Route.fullPath,
            to: BuyCreditsRoute.fullPath,
            state: {
              routeStates: {
                [BuyCreditsRoute.id]: {
                  neededCredits,
                },
              },
            },
          };

          if (navigate) {
            navigate(navigateOptions);
          } else {
            throw redirect(navigateOptions);
          }
          return;
        }

        if (newCampaignState.planning.type === "OnDemand") {
          const boostCreditEstimate =
            (newCampaignState.planning.leadAmount ?? 0) *
            campaignLeadCost.leadCost;
          if (boostCreditEstimate > 0) {
            const confirmed = await confirm(boostCreditEstimate);
            if (!confirmed) return;
          }
        }
      }

      const createdCampaign = await campaignsApi.create(
        createCampaignParams,
        context.trialPeriod
          ? {
              startNow: true,
              sendNow: true,
            }
          : {
              startNow: newCampaignState.planning.type === "OnDemand",
              sendNow: false,
            },
      );

      if (
        newCampaignState.planning.type === "OnDemand" &&
        newCampaignState.planning.leadAmount
      ) {
        const campaignRunOptions = {
          amount: newCampaignState.planning.leadAmount,
          deliverContacts: newCampaignState.planning.deliverContacts ?? false,
          deliverContactsRequirePhone:
            newCampaignState.planning.deliverContactsRequirePhone ?? false,
          deliverLiveNumber:
            newCampaignState.planning.deliverLiveNumber ?? false,
          completionEmail: newCampaignState.delivery.completionEmail ?? null,
          adjustAmountIfTooMany: true,
        };

        const deliveryTrackingParams = {
          source: "Campaign creation",
          campaignId: createdCampaign.id,
          campaignType: newCampaignState.planning.type,
          options: campaignRunOptions,
        } as const;

        try {
          const campaignRun = await campaignsApi.startCampaignRun(
            createdCampaign.id,
            campaignRunOptions,
          );

          trackDeliveryScheduled(context.user.id, {
            ...deliveryTrackingParams,
            requestedLeads: campaignRun.requestedLeads,
          });

          if (
            campaignRun.requestedLeads < newCampaignState.planning.leadAmount
          ) {
            const toastId = toast("Ikke nok leads i målgruppen", {
              description: `Du modtager i stedet ${campaignRun.requestedLeads} leads, som svarer til størrelsen på målgruppen.`,
              duration: Infinity,
              action: {
                label: "Forstået",
                onClick: () => {
                  toast.dismiss(toastId);
                },
              },
            });
          }
        } catch (error) {
          trackDeliveryScheduled(context.user.id, {
            ...deliveryTrackingParams,
            error,
          });

          toast.error("Noget gik galt", {
            description:
              "Din kampagne er oprettet, men der opstod en fejl ved start af leveringen. Det kan være nødvendigt at starte en manuel levering fra kampagnesiden.",
            duration: 10000,
            dismissible: true,
          });

          Sentry.captureException({
            title: "Error in startCampaignRun after campaign creation",
            error,
          });
        }

        markExpectedCampaignRun(createdCampaign.id);
      }

      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["simpleCampaigns"] });

      toast.success("Kampagne oprettet", {
        description: `${
          createdCampaign.chosenStartDate
            ? formatDate(createdCampaign.chosenStartDate) + " - "
            : ""
        }"${createdCampaign.name}"`,
        dismissible: true,
      });

      store.set(newlyCreatedCampaignIdAtom, createdCampaign.id);

      trackEvent("Campaign creation completed", context.user.id, {
        draftId: newCampaignState.draftId,
        campaignId: createdCampaign.id,
        campaignType: newCampaignState.planning.type,
        deliverContacts: contactInformationValues.deliverContacts,
        deliverContactsRequirePhone:
          contactInformationValues.deliverContactsRequirePhone,
        deliverLiveNumber: contactInformationValues.deliverLiveNumber,
      });

      if (navigate) {
        await navigate({
          to: "/campaigns/$id",
          params: { id: createdCampaign.id },
          ignoreBlocker: true,
        });

        // Deletes the localstorage state
        store.set(newCampaignStateAtom, RESET);
      } else {
        // Deletes the localstorage state
        store.set(newCampaignStateAtom, RESET);

        throw redirect({
          to: "/campaigns/$id",
          params: { id: createdCampaign.id },
          ignoreBlocker: true,
        });
      }
    };

    return {
      parseHistoryState,
      useHistoryState: () => historyState,
      createCampaign,
    };
  },
  loader: async ({ context }) => {
    await context.queryClient.prefetchQuery(simpleCampaignsQueryOptions);
    return { hasCampaignSetupPadding: false };
  },
});

const campaignTypeNameMap = entriesToRecord(
  planningTypes.map((dM) => [dM.type, dM.name]),
);

const deliveryTypeNameMap = entriesToRecord(
  deliveryTypes.map((dM) => [dM.type, dM.name]),
);

function RouteComponent() {
  const context = Route.useRouteContext();
  const navigate = Route.useNavigate();

  const historyState = context.useHistoryState();

  const { context: stepperContext } = context.useStepper();

  const { data: usersQueryData, isPending } = useUsersQuery({});
  const { data: integrations } = useAvailableIntegrationsQuery();
  const { data: simpleCampaigns } = useSuspenseQuery(
    simpleCampaignsQueryOptions,
  );

  const findUser = useCallback(
    (userId: string) => {
      if (isPending) return null;
      return (
        usersQueryData?.pages
          .flatMap((p) => p.results)
          .find((u) => u.id === userId) ?? null
      );
    },
    [usersQueryData, isPending],
  );

  const triggers = useMemo(() => {
    return context.appConfiguration.triggerTypes
      .filter((t) =>
        stepperContext.planning?.triggers?.some((e) => e.event === t.event),
      )
      .map((t) => t.label)
      .join(", ");
  }, [
    context.appConfiguration.triggerTypes,
    stepperContext.planning?.triggers,
  ]);

  const getIntegration = useCallback(
    (integration: string) => {
      if (!integrations) return null;
      return (
        integrations.find(
          (i) => i.uniqueName.toLowerCase() === integration.toLowerCase(),
        ) ?? null
      );
    },
    [integrations],
  );

  const getDeliverySummary = useCallback(
    (delivery: NewCampaignStateDelivery | null) => {
      const items = [
        {
          title: "Destination",
          value: delivery?.type ? deliveryTypeNameMap[delivery.type] : "-",
        },
      ];

      switch (delivery?.type) {
        case "Excel":
          items.push({
            title: "Sendes til",
            value: delivery.completionEmail ?? "-",
          });
          break;

        case "Integration":
          items.push(
            {
              title: "Integration",
              value: getIntegration(delivery.exportSystem ?? "")?.name ?? "-",
            },
            {
              title: "Type",
              value:
                delivery.exportMode === "SalesAutomatic"
                  ? "Alle leads"
                  : delivery.exportMode === "SalesManual"
                    ? "Kvalifererede leads"
                    : "-",
            },
          );
          break;
      }

      return items;
    },
    [getIntegration],
  );

  const getLeadsLabel = useCallback(
    (planning: NewCampaignState["planning"]): string => {
      switch (planning?.type) {
        case "Scheduled":
          return formatScheduleLabel(planning.schedules);
        case "Triggered":
          return planning.maxLeadsPerWeek
            ? formatNumber(planning.maxLeadsPerWeek)
            : "-";
        case "OnDemand":
          return planning.leadAmount ? formatNumber(planning.leadAmount) : "-";
        default:
          return "-";
      }
    },
    [],
  );

  const getPriorityLabel = useCallback(
    (configurations: NewCampaignState["configurations"]): string => {
      const { descendingOrder, orderBy } = getCampaignPriority(configurations);
      return (
        Object.values(PRIORITY_OPTIONS).find(
          (option) =>
            option.orderBy === orderBy &&
            option.descendingOrder === descendingOrder,
        )?.label ?? "-"
      );
    },
    [],
  );

  const getDateRangeLabel = useCallback(
    (planning: NewCampaignState["planning"]): string => {
      if (!planning || !isOneOf(planning.type, "Scheduled", "Triggered"))
        return "-";

      const start = planning.chosenStartDate;
      const end = planning.chosenEndDate;
      if (!start && !end) return "-";

      const startStr = start ? formatDate(start.toString()) : "";
      const endStr = end ? formatDate(end.toString()) : "";

      if (startStr && endStr) return `${startStr} - ${endStr}`;
      return startStr || endStr;
    },
    [],
  );

  const summary = useMemo<Summary>(() => {
    const detailRows: SummaryRow[] = [
      {
        title: "Navn",
        value:
          stepperContext.name ||
          getFinalCampaignName(stepperContext.name ?? "-", simpleCampaigns),
      },
      {
        title: "Ansvarlig",
        value: stepperContext.responsibleId
          ? (findUser(stepperContext.responsibleId)?.name ?? "-")
          : "-",
      },
    ];

    const planningRows: SummaryRow[] = [
      {
        title: "Kampagnetype",
        value: stepperContext.planning?.type
          ? campaignTypeNameMap[stepperContext.planning.type]
          : "-",
      },
      {
        title: "Leads",
        value: getLeadsLabel(stepperContext.planning),
      },
    ];

    if (stepperContext.planning?.type === "Triggered") {
      planningRows.push({
        title: "Triggers",
        value: triggers || "-",
      });
    }

    if (isOneOf(stepperContext.planning?.type, "OnDemand", "Scheduled")) {
      if (stepperContext.configurations.filter((c) => c.enabled).length > 1) {
        planningRows.push({
          title: "Strategi",
          value: detectCurrentStrategy(stepperContext.configurations).title,
        });
      }
      planningRows.push({
        title: "Prioritet",
        value: getPriorityLabel(stepperContext.configurations),
      });
    }

    if (isOneOf(stepperContext.planning?.type, "Scheduled", "Triggered")) {
      planningRows.push({
        title: "Periode",
        value: getDateRangeLabel(stepperContext.planning),
      });
    }

    return [
      { title: "Detaljer", rows: detailRows },
      { title: "Planlægning", rows: planningRows },
      { title: "Levering", rows: getDeliverySummary(stepperContext.delivery) },
    ];
  }, [
    findUser,
    getDeliverySummary,
    getLeadsLabel,
    getPriorityLabel,
    getDateRangeLabel,
    stepperContext,
    simpleCampaigns,
    triggers,
  ]);

  const mainConfiguration = useMemo(
    () => stepperContext.configurations.find((c) => c.quality === 1),
    [stepperContext.configurations],
  );

  const filterGroups = useMemo(
    () => getFilterGroupsFromConfigurations(stepperContext.configurations),
    [stepperContext.configurations],
  );

  const campaignStartsInstantly = useMemo(
    () => doesCampaignStartInstantly(stepperContext),
    [stepperContext],
  );

  const [confirmDialogProps, setConfirmDialogProps] = useState<ComponentProps<
    typeof ConfirmBoostDialog
  > | null>(null);

  const createCampaignMutation = useMutation({
    mutationKey: ["createCampaign"],
    mutationFn: async ({ ignoreCredits }: { ignoreCredits?: boolean }) => {
      await queryClient.ensureQueryData(
        context.appliedConfigurationSuggestionsQueryOptions,
      );
      await context.createCampaign({
        ignoreCredits,
        navigate,
        confirm: async (maxCredits) => {
          const { promise, resolve } = new DeferredPromise<boolean>();

          setConfirmDialogProps({
            maxCredits,
            onConfirm: () => {
              setConfirmDialogProps(null);
              resolve(true);
            },
            onCancel: () => {
              setConfirmDialogProps(null);
              resolve(false);
            },
          });

          return promise;
        },
      });
    },
  });

  const submit = useCallback(async () => {
    if (createCampaignMutation.isPending) return;

    if (!stepperContext.planning || !stepperContext.delivery)
      throw Error("Incomplete newCampaignState");

    await createCampaignMutation.mutateAsync({
      ignoreCredits: historyState?.autoSubmit,
    });
  }, [
    createCampaignMutation,
    historyState?.autoSubmit,
    stepperContext.delivery,
    stepperContext.planning,
  ]);

  const autoSubmitting = useRef(false);

  useEffect(() => {
    if (autoSubmitting.current || !historyState?.autoSubmit) return;
    autoSubmitting.current = true;
    submit().finally(() => {
      autoSubmitting.current = false;
    });
  }, [historyState?.autoSubmit, submit]);

  context.stepper.useStepperSubmit({
    submit,
    text: campaignStartsInstantly ? "Start kampagne" : "Opret kampagne",
    isDisabled: createCampaignMutation.isPending,
    isPending: createCampaignMutation.isPending,
  });

  return (
    <div key="summary" className="flex size-full">
      <div className="flex grow flex-col items-center justify-center gap-y-8 p-8 xl:p-14">
        <SegmentHeader
          className="max-w-md"
          title="Opsummering"
          description="Her ser du en oversigt over din kampagne. Verificer venligst, at alt er som du ønsker, inden du gemmer den."
        />

        <div className="mx-auto flex w-full max-w-120 flex-col gap-y-8">
          {summary.map((section, i) => (
            <div key={i} className="flex shrink-0 grow flex-col gap-y-6">
              <div className="flex flex-col gap-y-4">
                <span className="font-semibold">{section.title}</span>
                <div className="bg-theme-on-background/5 grid w-full grid-cols-2 gap-x-4 gap-y-8 rounded p-4">
                  {section.rows.map((row, i) => (
                    <React.Fragment key={i}>
                      <div className="text-left text-xs font-normal">
                        {row.title}
                      </div>
                      <div className="text-right text-xs font-semibold">
                        {row.value}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="h-full py-3">
        <Separator orientation="vertical" className="h-full" />
      </div>

      <div className="flex h-full w-72 shrink-0 grow-0 flex-col px-8">
        <PageHeader className="px-0 xl:px-0" title="Målgruppe" sticky={false} />

        <div className="-m-4 flex flex-col gap-y-4 py-12">
          <div className="flex flex-col gap-y-2 p-4 text-xs">
            <div className="font-semibold">Stoplister</div>
            <div className="text-theme-on-background-variant font-medium">
              {stepperContext.stopLists && stepperContext.stopLists.length > 0
                ? "Aktiveret"
                : "Deaktiveret"}
            </div>
          </div>
          {Array.from(filterGroups.keys()).map((filterGroupId) => {
            const filterGroup = getFilterGroupById(filterGroupId);
            if (!filterGroup) return null;

            const { group } = filterGroup;

            return (
              <div
                className="flex flex-col gap-y-2 p-4 text-xs"
                key={filterGroupId}
              >
                <div className="font-semibold">{group.title}</div>
                <div className="text-theme-on-background-variant font-medium">
                  {group.DisplayValue({
                    filters: mainConfiguration?.query.filters ?? [],
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {confirmDialogProps && <ConfirmBoostDialog {...confirmDialogProps} />}
    </div>
  );
}
